import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiListResponse, ApiResponse } from '../lib/response';
import { GiftCard } from '../models/giftCard';

export class GiftCardService extends BaseService<GiftCard> {
  override model = GiftCard;
  override modelName = 'giftCard';
  override modelLabel = 'Gift Card';

  async email(
    giftCard: GiftCard,
    data: { fromName: string; toName: string; toEmail: string; message: string },
  ): Promise<boolean> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${giftCard.objectID()}/email`,
      data: { ...data, body: data.message },
    });

    const response = await this.request(request);
    return response.success;
  }

  async search(
    query: string,
  ): Promise<ApiListResponse<GiftCard>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/search`,
      data: { query },
    });

    return await this.requestList(request, GiftCard);
  }

  async outstanding(): Promise<any> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/outstanding`,
    });

    const response = await this.request(request);
    return response.data;
  }

  /**
   * List gift cards with no assigned customer (i.e. claimable / unpaid). Hits
   * the dedicated `GET /giftCard/available` endpoint — narrower than `list()`
   * and avoids client-side filtering on a `claimable` field.
   */
  async available(): Promise<ApiListResponse<GiftCard>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/available`,
    });

    return await this.requestList(request, GiftCard);
  }

  /**
   * Filter gift cards server-side and return the intersection of matching
   * IDs (AND across all active filters). Backend route
   * `POST /giftCard/filter` expects each filter as a `{filter, params}`
   * entry and returns one matched ID list per entry — see
   * `app/Models/Business/Filters/GiftCardFilter.php` for available keys
   * (`query`, `expiry`, `claimable`, `empty`, `isPaid`). We transform a
   * flat `{key: params}` object (as produced by `useFilterController.getFilterParams()`)
   * into that envelope and intersect the per-filter ID lists, mirroring the
   * shape `customerPass.filter()` / `product.filter()` return.
   */
  async filter(filters: Record<string, any>): Promise<ApiResponse<{ ids: number[] }>> {
    const filterArray = Object.entries(filters).map(([filter, params]) => ({
      filter,
      params,
    }));

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/filter`,
      data: { filters: filterArray },
    });

    const response = await this.request(request);
    const results = (response.data?.filters ?? []) as Array<{ ids: number[] }>;

    let ids: number[] = [];
    if (results.length > 0) {
      const sets = results.map((r) => new Set(r.ids));
      const first = sets[0]!;
      ids = [...first].filter((id) => sets.every((s) => s.has(id)));
    }

    return { ...response, data: { ids } } as ApiResponse<{ ids: number[] }>;
  }
}
